/**
 * Toast Store
 *
 * Centralized toast notification management using vue-sonner.
 * Provides a clean API for showing success, error, warning, and info toasts.
 *
 */

import { defineStore } from "pinia";
import { toast } from "vue-sonner";

export const useToastStore = defineStore("toast", () => {
    //  Show a success toast
    const success = (message: string, description?: string) => {
        toast.success(message, { description });
    };

    //  Show an error toast
    const error = (message: string, description?: string) => {
        toast.error(message, { description });
    };

    //  Show an info toast
    const info = (message: string, description?: string) => {
        toast.info(message, { description });
    };

    //  Show a warning toast
    const warning = (message: string, description?: string) => {
        toast.warning(message, { description });
    };

    //  Show a loading toast
    const loading = (message: string) => {
        return toast.loading(message);
    };

    /**
     * Show a promise toast with loading/success/error states
     * @param promise - Promise to track
     * @param messages - Messages for each state
     */
    const promise = <T>(
        promise: Promise<T> | (() => Promise<T>),
        messages: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: any) => string);
        },
    ) => {
        return toast.promise(promise, messages);
    };

    /**
     * Dismiss a specific toast by ID
     * @param toastId - ID of toast to dismiss
     */
    const dismiss = (toastId?: string | number) => {
        toast.dismiss(toastId);
    };

    return {
        success,
        error,
        info,
        warning,
        loading,
        promise,
        dismiss,
    };
});
